<template>
  <div>
    <FeaturedPublication v-if="entry.kind === 'research'" :entry="entry" :embedded="embedded" />
    <template v-else>
    <header v-if="!embedded" class="collection-detail-head">
      <div>
        <span class="collection-detail-kicker">{{ entry.category }} · {{ entry.period }}</span>
        <h3>{{ entry.title }}</h3>
        <p v-if="entry.subtitle" class="collection-detail-subtitle">{{ entry.subtitle }}</p>
      </div>
      <button type="button" class="collection-detail-close" aria-label="Close details" @click="$emit('close')">×</button>
    </header>
    <div class="collection-detail-layout" :class="{ 'has-project-media': hasProjectMedia, 'has-signature-media': Boolean(showcase), 'has-small-media': ['dealtracker', 'lexaday'].includes(entry.id) }">
    <div class="collection-detail-copy">
    <p v-if="embedded && showSubtitle && entry.subtitle && entry.kind !== 'project'" class="collection-detail-embedded-subtitle">{{ entry.subtitle }}</p>
    <div v-if="entry.lab" class="collection-detail-lab">
      <span>Associated with <a :href="entry.lab.url" target="_blank" rel="noopener"><b>{{ entry.lab.name }}</b></a></span>
      <span v-if="entry.lab.logo" class="collection-detail-logo">
        <img :src="entry.lab.logo" :style="{ height: entry.lab.logoHeight || '4rem' }" :alt="entry.lab.name">
      </span>
    </div>
    <div v-if="showcase" class="collection-detail-body"><p><ArchiveTermText v-if="entry.id === 'searchstory'" :text="showcase.description" /><template v-else>{{ showcase.description }}</template></p></div>
    <div v-else class="collection-detail-body" v-html="entry.body"></div>
    <ProjectEntryActions v-if="entry.kind === 'project'" :project-id="entry.id" />
    <footer v-else-if="entry.kind !== 'research' && (entry.status || entry.route || (entry.links && entry.links.length) || (entry.extraLinks && entry.extraLinks.length))" class="collection-detail-footer">
      <span v-if="entry.status && entry.kind !== 'research'" class="collection-detail-status">{{ entry.status }}</span>
      <a v-if="entry.route && entry.externalPage" class="collection-project-link" :href="entry.route" target="_blank" rel="noopener"><span>{{ entry.action || 'Open project' }}</span><span class="collection-project-arrow" aria-hidden="true"><ArrowUpRightIcon /></span></a>
      <router-link v-else-if="entry.route" class="collection-project-link" :to="entry.route">
        <span>{{ entry.action || 'Open project' }}</span>
        <span class="collection-project-arrow" aria-hidden="true"><SiteIcon name="arrow-right" /></span>
      </router-link>
      <GitHubRepoLink
        v-for="link in entry.extraLinks || []"
        :key="link.url"
        :href="link.url"
        :label="link.label"
        :icon-class="link.iconClass || 'fab fa-github'"
      />
    </footer>
    </div>
    <LiveDeckEmbed v-if="isLiveDemo" class="collection-live-preview" @click.stop @keydown.stop />
    <ProjectShowcase v-else-if="showcase" :project-id="entry.id" :show-caption="false" :show-controls="false" class="collection-showcase-preview" />
    <FigureSequence v-else-if="hasProjectMedia" :images="figures" :title="entry.title" :initial-label="initialFigure" side-tabs compact />
    <SourceViewLinks v-if="entry.sourceId" :source-id="entry.sourceId" />
    </div>
    </template>
  </div>
</template>

<script>
import ProjectEntryActions from './ProjectEntryActions.vue';
import FeaturedPublication from './FeaturedPublication.vue';
import FigureSequence from './FigureSequence.vue';
import ProjectShowcase from './ProjectShowcase.vue';
import SourceViewLinks from './SourceViewLinks.vue';
import GitHubRepoLink from './GitHubRepoLink.vue';
import { projectShowcases } from '../../data/projectShowcases';
import LiveDeckEmbed from '../view/LiveDeckEmbed.vue';
import ArchiveTermText from './ArchiveTermText.vue';

export default {
  name: 'CollectionDetail',
  components: { FeaturedPublication, FigureSequence, ProjectShowcase, SourceViewLinks, GitHubRepoLink, ProjectEntryActions, LiveDeckEmbed, ArchiveTermText },
  props: {
    entry: {
      type: Object,
      required: true
    },
    embedded: {
      type: Boolean,
      default: false
    },
    showSubtitle: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  computed: {
    showcase() { return this.entry.showcase ? projectShowcases[this.entry.id] : null; },
    figures() {
      const showcase = this.showcase;
      return showcase ? showcase.scenes.map(scene => ({ ...scene, caption: '', fullSrc: scene.src, src: scene.smallSrc })) : this.entry.images || [];
    },
    initialFigure() { return this.entry.showcase ? projectShowcases[this.entry.id]?.defaultScene || '' : ''; },
    isLiveDemo() { return this.entry.id === 'scrollytelling'; },
    hasProjectMedia() { return this.isLiveDemo || this.figures.length > 0; }
  }
};
</script>

<style scoped>
.collection-detail-layout { container-type: inline-size; }
.has-project-media { display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr); grid-template-rows: min-content minmax(0, 1fr); align-items: start; gap: 20px clamp(28px, 4vw, 64px); padding: 20px 0 10px; }
.has-signature-media { grid-template-columns: minmax(0, .9fr) minmax(0, 1.25fr); }
.has-small-media { grid-template-columns: minmax(0, 1.25fr) minmax(0, .9fr); }
.collection-detail-copy { min-width: 0; }
.collection-detail-layout > .source-view-links { grid-column: 1 / -1; width: min(100%, 420px); }
.has-project-media > .source-view-links { margin-top: 0; }
.has-project-media .collection-detail-body { margin-top: 0; }
.has-project-media .collection-detail-body :deep(p:first-child) { margin-top: 0; }
.has-project-media .collection-detail-embedded-subtitle { margin: 0 0 16px; }
.collection-detail-layout :deep(.project-entry-actions) { flex-direction: column; align-items: flex-start; justify-content: flex-start; gap: 4px; margin-top: 20px; }
.collection-detail-layout :deep(.project-entry-primary) { margin-right: 0; }
.has-project-media :deep(.figure-sequence) { margin: 0; }
.has-signature-media :deep(.figure-sequence.is-compact .figure-sequence-note) { min-height:0; }
.has-project-media > .collection-detail-copy { grid-column: 1; grid-row: 1; }
.has-project-media > .figure-sequence,
.has-project-media > .collection-showcase-preview,
.has-project-media > .collection-live-preview { grid-column: 2; grid-row: 1 / 3; }
.has-project-media > .collection-live-preview { min-width: 0; margin: 0; }
.has-small-media > .figure-sequence { --figure-preview-height: clamp(190px, 60cqw, 230px); max-width: 360px; justify-self: end; }
.has-project-media > .source-view-links { grid-column: 1; grid-row: 2; width: 100%; max-width: 360px; align-self: start; }
@media (max-width: 820px) {
  .has-project-media { grid-template-columns: minmax(0, 1fr); gap: 24px; padding-top: 14px; }
  .has-project-media :deep(.figure-sequence) { width: min(100%, 480px); justify-self: center; }
  .has-small-media > .figure-sequence { width: min(100%, 340px); }
  .has-project-media { grid-template-rows: auto; }
  .has-project-media > .collection-detail-copy,
  .has-project-media > .figure-sequence,
  .has-project-media > .collection-showcase-preview,
  .has-project-media > .collection-live-preview,
  .has-project-media > .source-view-links { grid-column: 1; grid-row: auto; }
}
</style>
